import { z } from 'zod';

/** Códigos de error del contrato. El cliente decide qué mostrar según el código. */
export const CODIGOS_ERROR = [
  'validacion',
  'credenciales_invalidas',
  'cuenta_desactivada',
  'cuenta_sin_activar',
  'demasiados_intentos',
  'refresh_invalido',
  'refresh_reutilizado',
  'token_invalido',
  'ya_activada',
  'solicitud_en_curso',
  'codigo_invalido',
  'contrasena_debil',
  'no_autenticado',
  'sin_permiso',
  'no_encontrado',
  'error_interno',
] as const;

export type CodigoError = (typeof CODIGOS_ERROR)[number];

export const esquemaError = z.object({
  error: z.object({
    codigo: z.enum(CODIGOS_ERROR),
    mensaje: z.string(),
    campos: z.record(z.string()).optional(),
  }),
});

export type RespuestaError = z.infer<typeof esquemaError>;

export const esquemaPaginacion = z.object({
  pagina: z.coerce.number().int().min(1).default(1),
  porPagina: z.coerce.number().int().min(1).max(200).default(50),
});

export type Paginacion = z.infer<typeof esquemaPaginacion>;

export const AMBITOS_ROL = ['copathy', 'empresa', 'trabajador'] as const;
export type AmbitoRol = (typeof AMBITOS_ROL)[number];

export const CLAVES_ROL = [
  'admin_copathy',
  'consultor_copathy',
  'admin_empresa',
  'trabajador',
] as const;
export type ClaveRol = (typeof CLAVES_ROL)[number];

/**
 * Claves de permiso. Son las mismas que consumen `usePuede` en la UI y
 * `requierePermiso` en la API: una sola lista, para que un typo no se convierta
 * en un permiso que nadie tiene y por lo tanto en una pantalla en blanco.
 */
export const CLAVES_PERMISO = [
  'empresas.ver',
  'empresas.crear',
  'empresas.editar',
  'trabajadores.ver',
  'trabajadores.cargar',
  'catalogos.administrar',
  'usuarios.administrar',
  'roles.administrar',
  'cuestionarios.ver',
  'cuestionarios.armar',
  'aplicaciones.ver',
  'aplicaciones.administrar',
  'resultados.ver_todas_empresas',
  'resultados.ver_propia_empresa',
  'respuestas.ver_individuales',
  'participacion.ver',
  'auditoria.ver',
  'actividades.responder',
  'historial.ver_propio',
  'solicitudes.ver',
  'solicitudes.decidir',
  'codigos.administrar',
] as const;
export type ClavePermiso = (typeof CLAVES_PERMISO)[number];

/**
 * Permisos que un rol de ámbito empresa o trabajador **no puede tener nunca**.
 *
 * ⚠️ **La fuente de verdad es la base**, en `permisos.prohibido_para_empresa`
 * (migración 009): es lo que hace cumplir el trigger. Esta constante es su
 * **espejo**, para que el frontend pueda avisar antes de mandar algo que va a
 * rebotar.
 *
 * Hasta la 009 era al revés —la lista vivía solo acá y el motor hacía cumplir uno
 * solo de los ocho—, o sea que era documentación disfrazada de control. Una
 * prueba compara esta lista contra la tabla: si se desincronizan, falla.
 */
export const PERMISOS_PROHIBIDOS_PARA_EMPRESA: readonly ClavePermiso[] = [
  'respuestas.ver_individuales',
  'resultados.ver_todas_empresas',
  'auditoria.ver',
  'empresas.crear',
  'empresas.editar',
  'usuarios.administrar',
  'roles.administrar',
  // Decidir quien entra es de Copathy (D-022). Si la empresa pudiera aprobar,
  // podria darse acceso a si misma a los datos de su gente.
  'solicitudes.decidir',
  'codigos.administrar',
  // La biblioteca de cuestionarios es de Copathy (M5). Que el cliente viera el
  // instrumento antes de que su gente lo responda es material para prepararlos,
  // y ahi la medicion deja de medir. Es la frontera 3 vista desde otro angulo.
  'cuestionarios.ver',
  'cuestionarios.armar',
  // Aplicar y cerrar es decision de Copathy: es quien programa la sesion. La
  // empresa vera participacion agregada cuando exista M8, no el aparato.
  'aplicaciones.ver',
  'aplicaciones.administrar',
];

export const PLATAFORMAS = ['web', 'ios', 'android'] as const;
export type Plataforma = (typeof PLATAFORMAS)[number];
